import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Paper,
    CircularProgress,
    Chip,
    Skeleton,
    Stack,
    Autocomplete,
    Alert,
    InputAdornment
} from '@mui/material';
import { Add, Delete, AccountBalance, CheckCircle, Search } from '@mui/icons-material';
import {
    useBanks,
    useBankAccounts,
    useVerifyAccount,
    useAddBankAccount,
    useDeleteBankAccount,
    useSetDefaultAccount
} from 'hooks/useBankAccounts';

export default function BankAccountManager({ onSelect, selectedAccountId, userId }) {
    const { data: accounts, isLoading: loadingAccounts } = useBankAccounts(userId);
    const { data: banks = [], isLoading: loadingBanks } = useBanks();
    const { mutate: addAccount, isPending: isAdding } = useAddBankAccount(userId);
    const { mutate: deleteAccount } = useDeleteBankAccount(userId);
    const { mutate: setDefault } = useSetDefaultAccount(userId);
    const verifyMutation = useVerifyAccount();

    const [openAdd, setOpenAdd] = useState(false);
    const [selectedBank, setSelectedBank] = useState(null);
    const [accountNumber, setAccountNumber] = useState('');
    const [verifiedAccount, setVerifiedAccount] = useState(null);
    const [verifyError, setVerifyError] = useState('');

    // Auto-trigger verification when 10-digit NUBAN is entered and bank is selected
    useEffect(() => {
        if (accountNumber.length === 10 && selectedBank) {
            setVerifiedAccount(null);
            setVerifyError('');
            verifyMutation.mutate(
                { accountNumber, bankCode: selectedBank.code },
                {
                    onSuccess: (data) => {
                        setVerifiedAccount(data);
                    },
                    onError: (err) => {
                        setVerifyError(err?.response?.data?.message || 'Could not verify account. Check the details.');
                    }
                }
            );
        } else {
            setVerifiedAccount(null);
            setVerifyError('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountNumber, selectedBank]);

    const handleClose = () => {
        setOpenAdd(false);
        setSelectedBank(null);
        setAccountNumber('');
        setVerifiedAccount(null);
        setVerifyError('');
    };

    const handleSave = () => {
        if (!verifiedAccount || !selectedBank) return;
        addAccount(
            {
                accountName: verifiedAccount.accountName,
                accountNumber: verifiedAccount.accountNumber || accountNumber,
                bankName: selectedBank.name,
                bankCode: selectedBank.code
            },
            { onSuccess: handleClose }
        );
    };

    if (loadingAccounts) {
        return (
            <Stack spacing={2}>
                <Skeleton variant="rectangular" height={72} sx={{ borderRadius: 2 }} />
                <Skeleton variant="rectangular" height={72} sx={{ borderRadius: 2 }} />
            </Stack>
        );
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={600}>
                    Saved Bank Accounts
                </Typography>
                <Button size="small" startIcon={<Add />} onClick={() => setOpenAdd(true)} variant="outlined">
                    Add Account
                </Button>
            </Box>

            {!accounts?.length ? (
                <Paper
                    sx={{
                        p: 3,
                        textAlign: 'center',
                        bgcolor: 'grey.50',
                        border: '1px dashed #ccc',
                        borderRadius: 2
                    }}
                >
                    <AccountBalance sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary" variant="body2">
                        No saved bank accounts yet. Add one to get started.
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={1.5}>
                    {accounts.map((acc) => {
                        const isSelected = selectedAccountId === acc._id;
                        return (
                            <Paper
                                key={acc._id}
                                onClick={() => onSelect && onSelect(acc)}
                                sx={{
                                    p: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    border: '1.5px solid',
                                    borderColor: isSelected ? 'primary.main' : 'grey.200',
                                    bgcolor: isSelected ? 'primary.50' : '#fff',
                                    borderRadius: 2,
                                    cursor: onSelect ? 'pointer' : 'default',
                                    transition: 'all 0.18s ease',
                                    '&:hover': {
                                        borderColor: 'primary.light',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                                    }
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Box
                                        sx={{
                                            p: 1,
                                            bgcolor: isSelected ? 'primary.main' : 'grey.100',
                                            color: isSelected ? '#fff' : 'primary.main',
                                            borderRadius: 1.5,
                                            display: 'flex',
                                            transition: 'all 0.18s ease'
                                        }}
                                    >
                                        <AccountBalance fontSize="small" />
                                    </Box>
                                    <Box>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="subtitle2" fontWeight={700}>
                                                {acc.bankName}
                                            </Typography>
                                            {acc.isDefault && (
                                                <Chip
                                                    label="Default"
                                                    size="small"
                                                    color="success"
                                                    icon={<CheckCircle style={{ fontSize: 12 }} />}
                                                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                                                />
                                            )}
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">
                                            {acc.accountNumber} &bull; {acc.accountName}
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    {!acc.isDefault && (
                                        <Button
                                            size="small"
                                            sx={{ textTransform: 'none', fontSize: '0.72rem' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDefault(acc._id);
                                            }}
                                        >
                                            Set Default
                                        </Button>
                                    )}
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (window.confirm(`Remove ${acc.bankName} account?`)) {
                                                deleteAccount(acc._id);
                                            }
                                        }}
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Paper>
                        );
                    })}
                </Stack>
            )}

            {/* Add Account Dialog */}
            <Dialog open={openAdd} onClose={handleClose} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ pb: 1 }}>Add Bank Account</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2.5}>
                        {/* Step 1: Select Bank */}
                        <Autocomplete
                            options={banks}
                            loading={loadingBanks}
                            getOptionLabel={(option) => option.name || ''}
                            value={selectedBank}
                            onChange={(_, newValue) => {
                                setSelectedBank(newValue);
                                setAccountNumber('');
                                setVerifiedAccount(null);
                                setVerifyError('');
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Bank"
                                    size="small"
                                    placeholder="Search bank name..."
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <InputAdornment position="start">
                                                    <Search fontSize="small" />
                                                </InputAdornment>
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                        endAdornment: (
                                            <>
                                                {loadingBanks && <CircularProgress size={16} />}
                                                {params.InputProps.endAdornment}
                                            </>
                                        )
                                    }}
                                />
                            )}
                        />

                        {/* Step 2: Enter NUBAN */}
                        <TextField
                            label="Account Number (NUBAN)"
                            size="small"
                            fullWidth
                            value={accountNumber}
                            onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                setAccountNumber(val);
                            }}
                            disabled={!selectedBank}
                            placeholder="Enter 10-digit account number"
                            helperText={
                                !selectedBank ? 'Select a bank first' : accountNumber.length < 10 ? `${accountNumber.length}/10 digits` : ''
                            }
                            InputProps={{
                                endAdornment: verifyMutation.isPending && (
                                    <InputAdornment position="end">
                                        <CircularProgress size={18} />
                                    </InputAdornment>
                                )
                            }}
                        />

                        {/* Step 3: Show verification result */}
                        {verifyError && (
                            <Alert severity="error" sx={{ py: 0.5 }}>
                                {verifyError}
                            </Alert>
                        )}

                        {verifiedAccount && (
                            <Alert severity="success" icon={<CheckCircle fontSize="small" />} sx={{ py: 0.5 }}>
                                <Typography variant="body2" fontWeight={700}>
                                    {verifiedAccount.accountName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Account verified via Monnify
                                </Typography>
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleSave} variant="contained" disabled={!verifiedAccount || isAdding}>
                        {isAdding ? <CircularProgress size={20} /> : 'Save Account'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

BankAccountManager.propTypes = {
    onSelect: PropTypes.func,
    selectedAccountId: PropTypes.string,
    userId: PropTypes.string
};
